<script setup>
import { onMounted, ref, watch } from "vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonTable from "@/components/table/ArgonTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import UserTypeBadge from "@/components/badges/UserType.vue";
import ESTATUS_ENUM from "@/Constans/EstatusEnum";
import ActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import ModalWarehouse from "./ModalWarehouse.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ConfirmModal from "@/components/modal/ConfirmModal.vue";
import { useConfirmModal } from "@/composables/useConfirmModal";
import { warehouseStore } from "@/_store/warehouseStore";
import { argonTableStore } from "@/_store/argonTableStore";
import { userStore } from "@/_store/userStore";
import { errorUtils, fileUtils } from "@/utils";

const store = warehouseStore();
const tableStore = argonTableStore();
const userStores = userStore();

const showModal = ref(false);
const selectedWarehouseId = ref(null);
const snackbar = ref(null);
const {
  showConfirmModal,
  confirmTitle,
  confirmText,
  confirmButtonText,
  cancelButtonText,
  confirmButtonClass,
  cancelButtonClass,
  confirmLoading,
  openConfirmModal,
  handleConfirmModal,
} = useConfirmModal();

const fieldFilters = ref([
  {
    field: "name",
    description: "Nombre",
  },
]);

const warehouseTypeOptions = Object.entries(ESTATUS_ENUM.warehouse).map(
  ([value, meta]) => ({
    label: meta?.label || value,
    value,
  })
);

const defaultFilters = ref([
  {
    field: "type",
    label: "Tipo",
    multi: false,
    options: warehouseTypeOptions,
  },
]);

const columns = ref([
  { header: "Nombre", field: "name", sort: false, position: "start" },
  { header: "Tipo", field: "type", sort: false, position: "center" },
  { header: "Dirección", field: "address", sort: false, position: "start" },
  { header: "Contacto", field: "contact", sort: false, position: "start" },
  {
    header: "",
    field: "actions",
    sort: false,
    position: "center",
    width: "w-1",
  },
]);

onMounted(() => {
  tableStore.clear();
  const savedFilters = userStores.filtersPage.warehouses?.filters || [];
  tableStore.changeFilters(savedFilters);
  reloadPage();
});

function reloadPage() {
  userStores.filtersPage.warehouses = {
    filters: tableStore.filters,
    filtersFavorite: tableStore.filtersFavorite,
  };

  store.getWarehouses(tableStore.pagination, tableStore.query, tableStore.sort);
}

async function exportWarehouses() {
  const bytes = await store.exportar(
    tableStore.pagination,
    tableStore.query,
    tableStore.sort
  );

  fileUtils.downloadBytesFile(
    bytes,
    fileUtils.generateDownloadFileName("xlsx")
  );
}

function onSortChanged() {
  tableStore.clearPagination();
  reloadPage();
}

function openNewWarehouseModal() {
  selectedWarehouseId.value = null;
  showModal.value = true;
}

function editWarehouse(warehouse) {
  selectedWarehouseId.value = warehouse?._id || null;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

function successSaveWarehouse() {
  showModal.value = false;
  reloadPage();
}

function openDeleteWarehousesConfirm(warehouseIds = tableStore.checkedList) {
  const normalizedWarehouseIds = Array.isArray(warehouseIds)
    ? warehouseIds.filter(Boolean)
    : [];
  if (!normalizedWarehouseIds.length) {
    snackbar.value = {
      title: "Error",
      message: "Debe seleccionar al menos un punto habitual.",
      type: "danger",
      color: "white",
    };
    return;
  }

  const isSingleWarehouse = normalizedWarehouseIds.length === 1;
  openConfirmModal({
    title: isSingleWarehouse
      ? "Estas seguro de querer eliminar este punto habitual?"
      : "Estas seguro de querer eliminar los puntos habituales seleccionados?",
    text: "No podra revertir esta accion.",
    confirmButtonText: "Eliminar",
    confirmButtonClass: "btn btn-xs ms-1 bg-gradient-danger",
    onConfirm: () => {
      store.deleteWarehouse(normalizedWarehouseIds);
    },
  });
}

function getPosition(field) {
  const column = columns.value.find((col) => col.field === field);
  return column?.position || "start";
}

function addressLabel(item) {
  return item?.address?.address || "-";
}

function contactLabel(item) {
  return item?.contact?.name || "-";
}

function getRowActionsMenuId(warehouseItem) {
  return `warehouse-row-actions-${String(warehouseItem?._id || "unknown").replace(
    /[^a-zA-Z0-9_-]/g,
    "_"
  )}`;
}

function getRowActions() {
  return [
    [{ label: "Editar", event: "edit" }],
    [{ label: "Eliminar", event: "delete", type: "danger" }],
  ];
}

function onRowAction(warehouseItem, action) {
  const actionEvent = action?.event;
  if (!actionEvent || !warehouseItem?._id) return;

  if (actionEvent === "edit") {
    editWarehouse(warehouseItem);
    return;
  }

  if (actionEvent === "delete") {
    openDeleteWarehousesConfirm([warehouseItem._id]);
  }
}

const closeSnackbar = () => {
  snackbar.value = null;
};

watch(
  () => store.warehouseDeleted,
  (value) => {
    if (value) {
      tableStore.clearSelection();
      reloadPage();
      snackbar.value = {
        title: "OK",
        message: "Punto habitual eliminado",
        type: "info",
        color: "white",
      };
    }
  }
);

watch(
  () => store.error,
  (value) => {
    if (!value) return;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
);
</script>

<template>
  <div class="card warehouses-table-view">
    <div class="card-body warehouses-table-body">
      <argon-table
        v-if="store.warehouses"
        :columns="columns"
        :items="store.warehouses"
        :field-filters="fieldFilters"
        :default-filters="defaultFilters"
        :show-config-option="true"
        :show-filters="true"
        :show-importar="false"
        @filter-changed="reloadPage"
        @on-reload="reloadPage"
        @sort-changed="onSortChanged"
        @page-changed="reloadPage"
        @new-item="openNewWarehouseModal"
        @on-click="editWarehouse"
        @export-data="exportWarehouses"
      >
        <template #buttons>
          <div
            class="dropdown d-inline"
            data-dropdown-static
            style="--dropdown-gap: 4px"
          >
            <argon-button
              id="navbarDropdownWarehouseActions"
              color="primary"
              variant="outline"
              class="dropdown-toggle me-2 btn-xs"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              aria-expanded="false"
            >
              <i class="fa fa-exchange me-sm-2"></i>Acciones
            </argon-button>
            <ul
              class="dropdown-menu z-index-1030 dropdown-menu-lg-start px-2 py-3"
              aria-labelledby="navbarDropdownWarehouseActions"
            >
              <li>
                <a
                  class="dropdown-item border-radius-md text-danger"
                  @click="() => openDeleteWarehousesConfirm()"
                >
                  Eliminar
                </a>
              </li>
            </ul>
          </div>
        </template>
        <template #default="{ item }">
          <argon-td :position="getPosition('name')" :text-primary="item.name" />
          <argon-td :position="getPosition('type')">
            <user-type-badge
              style="min-width: 100px"
              class="badge badge-md"
              :item="item.type"
            />
          </argon-td>
          <argon-td
            :position="getPosition('address')"
            :text-primary="addressLabel(item)"
            max-width="280px"
          />
          <argon-td
            :position="getPosition('contact')"
            :text-primary="contactLabel(item)"
          />
          <argon-td :position="getPosition('actions')">
            <div class="table-row-actions">
              <actions-dropdown
                :menu-id="getRowActionsMenuId(item)"
                :actions="getRowActions()"
                aria-label="Acciones del punto habitual"
                trigger-class="table-action-button"
                menu-class="table-action-menu-dropdown"
                @action="(action) => onRowAction(item, action)"
              />
            </div>
          </argon-td>
        </template>
      </argon-table>
    </div>

    <modal-warehouse
      v-if="showModal"
      :warehouse-id="selectedWarehouseId"
      @close="closeModal"
      @success="successSaveWarehouse"
    />
    <confirm-modal
      v-if="showConfirmModal"
      v-model="showConfirmModal"
      :title="confirmTitle"
      :text="confirmText"
      :confirm-button-text="confirmButtonText"
      :cancel-button-text="cancelButtonText"
      :confirm-button-class="confirmButtonClass"
      :cancel-button-class="cancelButtonClass"
      :loading="confirmLoading"
      @confirm="handleConfirmModal"
    />

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </div>
</template>

<style scoped>
:deep(thead th.text-center.w-1:last-child),
:deep(tbody td.text-center:last-child) {
  max-width: 48px;
  min-width: 48px;
  width: 48px;
}
</style>
